import { useEffect, useMemo, useState } from 'react';
import { message, Skeleton } from 'antd';

import styles from './stylesUserWalletsGraph.module.css';
import { Line } from 'react-chartjs-2';
import { BRAND_ICONS_URLS, DATE_RANGE } from '../../../../utils/Constants';
import { CaretDownOutlined, CaretUpOutlined, DollarOutlined } from '@ant-design/icons';
import CustomDatePicker from '../../../CommonComponents/CustomDatePicker/CustomDatePicker';
import {
  chartOptions,
  formatMetricCurrentValue,
  formatMetricPercentage,
  getMetricLabelByKey,
  primaryColor,
} from '../../../../utils/Users/UserWalletsGraphUtils';
import { useUserWalletGraph } from '../../../../queries/useUserWalletGraph';
import { AppStrings } from '../../../../utils/constants/AppStrings';
import { getAdminNow } from "../../../../utils/dateService";

const getWalletGraphErrorMessage = (error, fallback) => {
  const data = error && typeof error === 'object' && 'response' in error
    ? error.response?.data
    : null;
  const raw =
    data && typeof data === 'object' && typeof data.message === 'string'
      ? data.message.trim()
      : '';
  if (raw) {
    return raw;
  }
  return fallback;
};

const getDefaultDateRange = () => [
  getAdminNow().subtract(29, 'day').startOf('day'),
  getAdminNow().endOf('day'),
];

function UserWalletsGraph({ userData, displaySign = false }) {
  const userId = userData?.user_id || userData?.id;
  const [selectedMetricKey, setSelectedMetricKey] = useState(null);
  const [dateRange, setDateRange] = useState(() => getDefaultDateRange());
  const startDate = dateRange?.[0] ?? null;
  const endDate = dateRange?.[1] ?? null;
  const {
    data: walletGraphData,
    isFetching,
    isError: isGraphError,
    error: graphQueryError,
  } = useUserWalletGraph({ userId, startDate, endDate });

  /**
   * Skeleton until the first payload exists (or error). Do not gate on isPending /
   * isFetching: for one frame both can be false while data is still undefined, which
   * briefly showed "No data" before.
   */
  const awaitingFirstChartPayload =
    Boolean(userId) && !isGraphError && !walletGraphData;

  const showGraphSkeleton = awaitingFirstChartPayload || isFetching;
  const isGraphLoading = isFetching;
  const graphErrorMessage =
    isGraphError && graphQueryError
      ? getWalletGraphErrorMessage(
          graphQueryError,
          AppStrings.FAILED_TO_LOAD_WALLET_GRAPH
        )
      : null;

  const availableMetricKeys = useMemo(
    () => walletGraphData?.metrics ?? [],
    [walletGraphData?.metrics]
  );

  /** Resolves immediately on the same render as data (useEffect runs too late — caused "No data" flash). */
  const displayMetricKey = useMemo(() => {
    if (!availableMetricKeys.length) return null;
    if (
      selectedMetricKey != null &&
      availableMetricKeys.includes(selectedMetricKey)
    ) {
      return selectedMetricKey;
    }
    return availableMetricKeys[0];
  }, [availableMetricKeys, selectedMetricKey]);

  useEffect(() => {
    const tabs = walletGraphData?.metrics;
    if (!tabs?.length) return;
    setSelectedMetricKey((prev) => (tabs.includes(prev) ? prev : tabs[0]));
  }, [walletGraphData?.metrics]);

  /** Only toast on API error; keep showing last successful chart via placeholderData. */
  useEffect(() => {
    if (!isGraphError || isGraphLoading || !graphErrorMessage) return;
    message.error(graphErrorMessage);
  }, [isGraphError, isGraphLoading, graphErrorMessage]);
  const selectedMetricLabel = getMetricLabelByKey(displayMetricKey);

  const totalEventsByDay = useMemo(
    () => {
      const selectedSeries =
        walletGraphData?.series?.[displayMetricKey] || [];
      return selectedSeries.map(([timestamp, event_count]) => ({
        date: new Date(timestamp).toISOString().slice(0, 10),
        event_count,
      }));
    },
    [displayMetricKey, walletGraphData]
  );

  const labels = totalEventsByDay.map((item) => item.date);
  const values = totalEventsByDay.map((item) => Number(item.event_count));
  const summaryEntry = walletGraphData?.summary?.[displayMetricKey];
  const currentValue = summaryEntry?.current ?? 0;
  const pctChange = summaryEntry?.pct_change;
  const percentageDisplay = formatMetricPercentage(pctChange, { displaySign });

  const data = {
    labels,
    datasets: [
      {
        label: selectedMetricLabel,
        data: values,
        borderColor: primaryColor,
        backgroundColor: primaryColor,
        borderRadius: 1,
        pointRadius: 0,
        pointHoverRadius: 0,
      },
    ],
  };

  const seriesPointCount =
    displayMetricKey != null
      ? (walletGraphData?.series?.[displayMetricKey]?.length ?? 0)
      : 0;
  const showLineChart = !showGraphSkeleton && seriesPointCount > 0;
  const showChartNoData =
    !showGraphSkeleton &&
    Boolean(userId) &&
    Boolean(walletGraphData) &&
    displayMetricKey != null &&
    seriesPointCount === 0;

  const setIconBasedOnMetric = (metricKey) => {
    switch (metricKey) {
      case 't3coin':
      case 'total_coins':
        return (
          <img
            src={process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
              BRAND_ICONS_URLS.secondaryCurrencySymbol}
            alt="Secondary currency icon"
            className={styles.userWalletsGraphHeaderContentLeftValueCurrencyIcon}
          />
        );
      case 'pikkem_cash':
      case 'pikkem_redeemable':
      case 'prediction_cash':
      case 'redeemable_prediction_cash':
        return (
          <DollarOutlined
            style={{ color: "var(--COLOR-PRIMARY)" }}
          />
        );
      case 't3cash':
      case 'redeemable_t3cash':
      case 'free_t3cash':
      case 'total_rebet_cash':
      case 'redeemable_rebet_cash':
      default:
        return (
          <img
            src={process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
              BRAND_ICONS_URLS.primaryCurrencySymbol}
            alt="Primary currency icon"
            className={styles.userWalletsGraphHeaderContentLeftValueCurrencyIcon}
          />
        );
    }
  };

  return (
    <div className={styles.userWalletsGraphContainer}>
      <div className={styles.userWalletsGraphHeader}>
        {availableMetricKeys.map((metricKey) => (
          <div
            className={`${styles.userWalletsGraphHeaderMetric} ${displayMetricKey === metricKey ? styles.selected : ''}`}
            key={metricKey}
            onClick={() => setSelectedMetricKey(metricKey)}
          >
            <p>{getMetricLabelByKey(metricKey)}</p>
          </div>
        ))}
      </div>
      <div className={styles.userWalletsGraphBody}>
        <div className={styles.userWalletsGraphHeaderContent}>
          <div className={styles.userWalletsGraphHeaderContentLeft}>
            {showGraphSkeleton ? (
              <Skeleton active paragraph={{ rows: 2 }} title={false} style={{ width: '300px' }} />
            ) : (
              <>
                <p className={styles.userWalletsGraphHeaderContentLeftTitle}>{selectedMetricLabel}</p>
                <div className={styles.userWalletsGraphHeaderContentLeftValueContainer}>
                  {setIconBasedOnMetric(displayMetricKey)}
                  <p
                    className={styles.userWalletsGraphHeaderContentLeftValue}
                    data-testid="wallet-graph-current-value"
                  >
                    {formatMetricCurrentValue(displayMetricKey, currentValue)}
                  </p>
                  {percentageDisplay && (
                    <p
                      className={`${styles.userWalletsGraphHeaderContentLeftValuePercentage} ${percentageDisplay.isPositive ? styles.percentagePositive : styles.percentageNegative}`}
                    >
                      {percentageDisplay.isPositive ? (
                        <CaretUpOutlined className={styles.percentageIcon} />
                      ) : (
                        <CaretDownOutlined className={styles.percentageIcon} />
                      )}
                      {percentageDisplay.text}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
          <div className={styles.userWalletsGraphHeaderContentRight}>
            <CustomDatePicker
              dates={dateRange}
              setDates={setDateRange}
              getData={() => { }}
              showPresets={true}
              startRange={DATE_RANGE.LAST_30_DAYS}
            />
          </div>
        </div>
        <div className={styles.userWalletsGraphBodyContent}>
          {showGraphSkeleton ? (
            <Skeleton active paragraph={{ rows: 8 }} title={false} />
          ) : showLineChart ? (
            <Line data={data} options={chartOptions} />
          ) : showChartNoData ? (
            <div className={styles.userWalletsGraphBodyContentNoData}>
              <p className={styles.userWalletsGraphBodyContentNoDataText}>
                {AppStrings.NO_DATA_AVAILABLE}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default UserWalletsGraph;